import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { FormControl } from '@mui/material'
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react'

import { SearchIcon } from '@assets/svgs/SearchIcon'
import { Tooltip } from '@components/shared/Tooltip'
import { Loading } from '../Loading'

import type { ComponentProps } from './types'

import * as S from './styles'

const DIACRITICS_PATTERN = new RegExp('[\\u0300-\\u036f]', 'g')

const normalize = (value: string) => value.normalize('NFD').replace(DIACRITICS_PATTERN, '').toLowerCase()

export const Component = ({
  name,
  label,
  placeholder,
  required,
  options,
  error,
  loading,
  tooltip,
  searchable,
  searchPlaceholder = 'Buscar...',
  ...rest
}: ComponentProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const selectWrapperRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<'left' | 'right'>('left')
  const [searchTerm, setSearchTerm] = useState('')
  const [searchWidth, setSearchWidth] = useState(0)
  const [menuWidth, setMenuWidth] = useState(0)

  const isDisabled = Boolean(rest.disabled)
  const fixedMenuWidth = Math.max(menuWidth, searchWidth)

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm) return options

    const normalizedTerm = normalize(searchTerm)
    return options.filter((item) => normalize(item.label).includes(normalizedTerm))
  }, [options, searchable, searchTerm])

  useEffect(() => {
    if (!open || !searchable) return

    const timeout = setTimeout(() => searchInputRef.current?.focus(), 0)
    return () => clearTimeout(timeout)
  }, [open, searchable])

  // Mede a largura do rótulo mais longo (lista completa, sem o filtro da busca) para travar o
  // menu num tamanho fixo, já que renderizar só as opções filtradas encolheria/cresceria o Paper
  useEffect(() => {
    if (!searchable) return

    setMenuWidth(measureRef.current?.getBoundingClientRect().width ?? 0)
  }, [options, searchable])

  const handleOpen = useCallback(() => {
    if (selectWrapperRef.current) {
      const rect = selectWrapperRef.current.getBoundingClientRect()
      const windowWidth = window.innerWidth
      const spaceOnRight = windowWidth - rect.right
      const minSpace = 250

      if (spaceOnRight < minSpace) {
        setMenuAnchor('right')
      } else {
        setMenuAnchor('left')
      }

      setSearchWidth(rect.width)
    }
    setOpen(true)
  }, [])

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    inputRef.current?.focus()
    handleOpen()
  }

  return (
    <FormControl disabled={isDisabled} fullWidth>
      <S.Label disabled={isDisabled}>
        {label}
        {required && ' *'}
      </S.Label>

      <S.SelectWrapper ref={selectWrapperRef}>
        <S.Select
          inputRef={inputRef}
          open={open}
          name={name}
          error={!!error}
          onOpen={handleOpen}
          onClose={() => {
            setOpen(false)
            setSearchTerm('')
          }}
          displayEmpty
          IconComponent={() => {
            return (
              <>
                {loading ? (
                  <Loading hasText={false} size="small" />
                ) : (
                  <S.IconBox error={!!error} isDisabled={isDisabled}>
                    {!open ? (
                      <CaretUpIcon weight="bold" onClick={!isDisabled ? handleIconClick : undefined} />
                    ) : (
                      <CaretDownIcon weight="bold" onClick={!isDisabled ? handleIconClick : undefined} />
                    )}
                  </S.IconBox>
                )}
              </>
            )
          }}
          MenuProps={{
            autoFocus: false,
            PaperProps: {
              component: S.MenuPaper,
              style: searchable
                ? { width: fixedMenuWidth, minWidth: fixedMenuWidth, maxWidth: fixedMenuWidth }
                : undefined
            },
            MenuListProps: { component: S.MenuList },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: menuAnchor
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: menuAnchor
            }
          }}
          renderValue={(selected) => {
            if (!selected || selected === '') return <S.Placeholder>{placeholder}</S.Placeholder>

            const selectedOption = options.find((item) => item.value === selected)

            return (
              <Tooltip title={tooltip || ''} placement="top">
                <S.SelectedValue>{selectedOption?.label}</S.SelectedValue>
              </Tooltip>
            )
          }}
          disabled={isDisabled || loading}
          {...rest}
        >
          {searchable && (
            <S.SearchBox
              onKeyDown={(e) => {
                if (e.key !== 'Escape') e.stopPropagation()
              }}
            >
              <S.SearchInputWrapper>
                <SearchIcon />
                <S.SearchInput
                  ref={searchInputRef}
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </S.SearchInputWrapper>
            </S.SearchBox>
          )}

          {searchable && filteredOptions.length === 0 ? (
            <S.MenuItem disabled>Nenhum resultado encontrado</S.MenuItem>
          ) : (
            filteredOptions.map((item) => (
              <S.MenuItem key={item.label} value={item.value}>
                {item.label}
              </S.MenuItem>
            ))
          )}
        </S.Select>

        {searchable && (
          <S.HiddenMeasurer ref={measureRef} aria-hidden>
            {options.map((item) => (
              <S.MenuItem key={`measure-${item.label}`} tabIndex={-1}>
                {item.label}
              </S.MenuItem>
            ))}
          </S.HiddenMeasurer>
        )}
      </S.SelectWrapper>

      {typeof error === 'string' && <S.ErrorMessage>{error}</S.ErrorMessage>}
    </FormControl>
  )
}
